import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  addMoney, Banner, Button, Card, Dialog, Field, formatMoney, mulMoney, PageHeader, percentOf, Pill, toUnits,
} from '@elysian/ui';
import type { Account, Contact, DocumentInput, InboxItem, LedgerDocument, TaxCode } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { isAmount, iso, longDate } from '../invoices/shared';
import { inboxState, readValue, supplierLabel } from './BillInbox';
import { CurrencyOptions, useYourCurrencies } from '../shared/CurrencyPicker';
import { rateFromPaid, rateHint, rateOk, trimRate, useDayRate } from '../shared/fx';

const SUSPENSE = '9999';

interface Loaded {
  item: InboxItem;
  suppliers: Contact[];
  accounts: Account[];
  codes: TaxCode[];
  draft: LedgerDocument | null;
  file: Blob | null;
}

export function BillReview() {
  const { id = '' } = useParams();
  const { source, today } = useSession();
  const [version, setVersion] = useState(0);
  const data = useAsync<Loaded>(async () => {
    const item = await source.inboxItem(id);
    const [suppliers, accounts, codes, draft, file] = await Promise.all([
      source.contacts('supplier'), source.accounts('expense'), source.taxCodes(iso(today())),
      item.draft_document_id ? source.document(item.draft_document_id) : Promise.resolve(null),
      source.attachmentFile(item.attachment_id).catch(() => null),
    ]);
    return { item, suppliers, accounts, codes: codes.filter((c) => c.applies_to !== 'sales'), draft, file };
  }, [source, id, version]);

  if (data.state === 'loading') return <PageHeader title="Bill" subtitle="Loading…" />;
  if (data.state === 'error') {
    return <><PageHeader title="Bill" /><Banner action={<Link to="/inbox">Back to the inbox</Link>}>Couldn’t open this bill: {data.error.message}</Banner></>;
  }
  return <Review key={`${id}:${version}`} {...data.data} reload={() => setVersion((v) => v + 1)} />;
}

interface Line { key: string; description: string; quantity: string; unit_price: string; account_code: string; tax_code: string; tax_amount: string }

function Review({ item, suppliers, accounts, codes, draft, file, reload }: Loaded & { reload: () => void }) {
  const { source, context } = useSession();
  const base = context?.organisation.base_currency ?? 'EUR';
  const toast = useToast((s) => s.show);
  const navigate = useNavigate();
  const [lines, setLines] = useState<Line[]>(() => (draft?.lines ?? []).map((l, i) => ({
    key: `l${i}`, description: l.description, quantity: trim(l.quantity ?? '1'), unit_price: trim(l.unit_price),
    account_code: l.account_code, tax_code: l.tax_code ?? 'S23', tax_amount: trim(l.tax_amount ?? ''),
  })));
  const [ref, setRef] = useState(draft?.supplier_ref ?? '');
  const [issue, setIssue] = useState(draft?.issue_date ?? '');
  const [due, setDue] = useState(draft?.due_date ?? '');
  // The supplier's currency, and how it becomes ours: the day's rate, a typed rate, or what the bank actually paid.
  const [currency, setCurrency] = useState(draft?.currency ?? base);
  const [typedRate, setTypedRate] = useState('');
  const [paid, setPaid] = useState('');
  const yourCurrencies = useYourCurrencies();
  const foreign = currency !== base;
  const dayRate = useDayRate(currency, issue, base);
  useEffect(() => {
    // A draft saved with its own rate keeps showing it.
    if (draft && draft.currency === currency && draft.issue_date === issue && draft.fx_rate && dayRate && typeof dayRate === 'object'
        && Math.abs(Number(draft.fx_rate) - dayRate.rate) > 0.0000005) {
      setTypedRate((t) => t || trimRate(draft.fx_rate!));
    }
  }, [dayRate, draft, currency, issue]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ack, setAck] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [supplierChoice, setSupplierChoice] = useState('');

  const state = inboxState(item);
  const who = supplierLabel(item, suppliers);
  const readGross = readValue(item, 'gross');
  const rate = (c: string) => codes.find((t) => t.code === c)?.rate ?? '0';
  const totals = useMemo(() => {
    const rows = lines.map((l) => {
      const ok = isAmount(l.quantity) && isAmount(l.unit_price);
      const net = ok ? mulMoney(l.quantity, l.unit_price) : '0';
      const tax = l.tax_amount.trim() && isAmount(l.tax_amount) ? addMoney(l.tax_amount) : percentOf(net, rate(l.tax_code));
      return { net, tax };
    });
    const net = addMoney('0', ...rows.map((r) => r.net));
    const tax = addMoney('0', ...rows.map((r) => r.tax));
    return { rows, net, tax, gross: addMoney(net, tax) };
  }, [lines, codes]);
  const paidRate = paid.trim() ? rateFromPaid(paid, totals.gross) : null;
  const rateText = paidRate ?? typedRate.trim();
  const rateValid = rateOk(rateText);
  const rateInUse = rateText && rateValid ? rateText : dayRate && typeof dayRate === 'object' ? String(dayRate.rate) : null;
  // As the ledger: the amount times the rate (up to 8 places), rounded to the cent.
  const inBase = (v: string) => (rateInUse ? (Math.round(Number(v) * Number(rateInUse) * 100) / 100).toFixed(2) : null);
  const money = (v: string) => formatMoney(v, currency);
  const rateProblem = !foreign ? null
    : paid.trim() && !paidRate ? 'Enter the amount the bank paid, such as 987.50'
    : !rateValid ? 'The exchange rate should be a number such as 0.8567'
    : !rateText && dayRate === 'none' ? `There’s no ${currency} rate for that date. Type one in, or the amount the bank paid.`
    : null;
  const mismatch = readGross && isAmount(readGross) && Math.abs(Number(toUnits(totals.gross) - toUnits(readGross))) > 200;
  const onSuspense = lines.filter((l) => l.account_code === SUSPENSE).length;
  const open = item.status === 'needs_review' || item.status === 'ready';

  const setLine = (key: string, patch: Partial<Line>) => {
    setDirty(true);
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const input = (): DocumentInput => ({
    type: 'purchase_bill', contact_id: draft!.contact_id, issue_date: issue, due_date: due || undefined,
    supplier_ref: ref.trim() || undefined, currency,
    ...(foreign && rateText && rateValid ? { fx_rate: rateText } : {}),
    lines: lines.map((l) => ({
      description: l.description.trim(), quantity: l.quantity.trim(), unit_price: l.unit_price.trim(),
      account_code: l.account_code, tax_code: l.tax_code, ...(l.tax_amount.trim() ? { tax_amount: l.tax_amount.trim() } : {}),
    })),
  });

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      if (/post anyway/i.test(message)) setAck(message);
      else setError(message);
    } finally {
      setBusy(false);
    }
  }

  const save = () => run(async () => {
    await source.replaceDraft(draft!.id, input());
    setDirty(false);
    toast('Changes saved');
    reload();
  });

  const approve = (acknowledge: boolean) => run(async () => {
    if (dirty) {
      await source.replaceDraft(draft!.id, input());
      setDirty(false);
    }
    const posted = await source.approveInbox(item.id, acknowledge);
    setAck(null);
    toast(`Bill ${posted.supplier_ref ?? ''} from ${who} approved and posted`.replace('  ', ' '));
    const next = (await source.inbox()).items.find((i) => i.id !== item.id && ['needs_supplier', 'needs_review', 'ready'].includes(i.status));
    navigate(next ? `/inbox/${next.id}` : '/inbox');
  });

  const reject = () => run(async () => {
    await source.rejectInbox(item.id, reason.trim());
    toast(`${item.file_name} removed from the inbox`);
    navigate('/inbox');
  });

  const chooseSupplier = () => run(async () => {
    await source.setInboxSupplier(item.id, supplierChoice === 'new' ? { create_from_extraction: true } : { contact_id: supplierChoice });
    reload();
  });

  return (
    <>
      <div style={{ marginBottom: 6 }}><Link to="/inbox" className="el-hint">← Bill inbox</Link></div>
      <PageHeader
        title={who}
        subtitle={<>{item.file_name}{readGross && <> · {formatMoney(readGross, currency)}</>}{item.email_from && <> · from {item.email_from}</>}</>}
        actions={item.status !== 'approved' && item.status !== 'rejected' ? (
          <>
            <Button variant="danger" onClick={() => { setReason(''); setRejecting(true); }} disabled={busy}>Not a bill</Button>
            {draft && dirty && <Button onClick={() => void save()} disabled={busy}>Save changes</Button>}
            {draft && <Button variant="primary" onClick={() => void approve(false)} disabled={busy || onSuspense > 0 || !open || rateProblem !== null}
              title={onSuspense ? 'Choose an account for every line first' : rateProblem ?? undefined}>Approve bill</Button>}
          </>
        ) : undefined}
      />
      <div className="el-row" style={{ marginBottom: 16 }}>
        <Pill tone={state.tone}>{state.label}</Pill>
        {item.match_method && <span className="el-hint">Supplier matched by {MATCH[item.match_method]}</span>}
      </div>
      {error && <div className="el-error-box" role="alert" style={{ marginBottom: 16 }}>{error}</div>}
      {(item.warnings?.length ?? 0) > 0 && !dirty && <Banner>{item.warnings!.join('. ')}.</Banner>}
      {item.error && (
        <Banner action={item.can_retry ? <Button size="sm" disabled={busy} onClick={() => void run(async () => {
          await source.retryInbox(item.id); toast(`${item.file_name} will be tried again`); reload();
        })}>Try again</Button> : undefined}>{item.error}</Banner>
      )}

      <div className="el-review-layout">
        <section aria-label="The bill as received"><BillFile item={item} file={file} who={who} draft={draft} /></section>

        <div className="el-stack">
          {item.status === 'needs_supplier' && (
            <Card as="section" padding="18px 20px" aria-labelledby="who">
              <h2 id="who" className="el-h2" style={{ marginBottom: 6 }}>Who is this from?</h2>
              <p className="el-hint" style={{ margin: '0 0 12px' }}>
                Read as “{readValue(item, 'supplier_name') ?? 'unknown'}”{readValue(item, 'supplier_vat_number') && <>, VAT {readValue(item, 'supplier_vat_number')}</>}.
                Your choice is remembered for this spelling next time.
              </p>
              <div className="el-row" style={{ alignItems: 'flex-end' }}>
                <div style={{ flex: '1 1 240px' }}>
                  <Field id="supplier" label="Supplier">
                    <select id="supplier" className="el-input" value={supplierChoice} onChange={(e) => setSupplierChoice(e.target.value)}>
                      <option value="">Choose…</option>
                      {readValue(item, 'supplier_name') && <option value="new">New supplier: {titleCase(readValue(item, 'supplier_name')!)}</option>}
                      {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </Field>
                </div>
                <Button variant="primary" onClick={() => void chooseSupplier()} disabled={!supplierChoice || busy}>Use this supplier</Button>
              </div>
            </Card>
          )}

          <Card as="section" padding="18px 20px" aria-labelledby="read">
            <h2 id="read" className="el-h2" style={{ marginBottom: 12 }}>What was read</h2>
            <dl className="el-read-list">
              {READ_FIELDS.map(([k, label]) => {
                const f = item.fields?.[k];
                if (!f) return null;
                const v = String(f.value);
                return (
                  <div key={k}>
                    <dt>{label}<Confidence value={f.confidence} /></dt>
                    <dd>{k === 'issue_date' || k === 'due_date' ? longDate(v) : ['net', 'vat', 'gross'].includes(k) ? formatMoney(v, currency) : v}</dd>
                  </div>
                );
              })}
            </dl>
          </Card>

        </div>
      </div>
      {/* The lines get the full width: seven columns don't fit beside the bill. */}
      <div style={{ marginTop: 16 }}>
          {draft && (
          <Card as="section" padding="18px 0 14px" aria-labelledby="bill">
            <div className="el-card-head"><h2 id="bill" className="el-h2">The bill to post</h2><span className="el-hint">Check the accounts; amounts are as read</span></div>
            <div className="el-form-grid" style={{ padding: '0 20px 14px' }}>
              <Field id="bill-ref" label="Supplier’s invoice no.">
                <input id="bill-ref" className="el-input" value={ref} disabled={!open} onChange={(e) => { setDirty(true); setRef(e.target.value); }} />
              </Field>
              <Field id="bill-issue" label="Invoice date">
                <input id="bill-issue" type="date" className="el-input" value={issue} disabled={!open} onChange={(e) => { setDirty(true); setIssue(e.target.value); }} />
              </Field>
              <Field id="bill-due" label="Due date">
                <input id="bill-due" type="date" className="el-input" value={due} disabled={!open} onChange={(e) => { setDirty(true); setDue(e.target.value); }} />
              </Field>
              <Field id="bill-currency" label="Currency" hint={foreign ? `The supplier is paid in ${currency}; the books record ${base}` : undefined}>
                <select id="bill-currency" className="el-input" value={currency} disabled={!open}
                  onChange={(e) => { setDirty(true); setCurrency(e.target.value); setTypedRate(''); setPaid(''); }}>
                  <CurrencyOptions base={yourCurrencies.base} yours={yourCurrencies.yours} />
                </select>
              </Field>
              {foreign && (
                <Field id="bill-rate" label={`Exchange rate (${base} per 1 ${currency})`}
                  error={!paid.trim() && !rateOk(typedRate) ? 'A number such as 0.8567' : null}
                  hint={paidRate ? 'Worked out from the amount the bank paid' : typedRate.trim() ? 'Your rate for this bill only. Clear it to use the day’s rate.' : rateHint(dayRate, currency, base)}>
                  <input id="bill-rate" className="el-input el-num" inputMode="decimal" disabled={!open || Boolean(paid.trim())}
                    value={paidRate ?? typedRate}
                    placeholder={dayRate && typeof dayRate === 'object' ? String(dayRate.rate) : dayRate === 'loading' ? '…' : 'Type a rate'}
                    onChange={(e) => { setDirty(true); setTypedRate(e.target.value); }} />
                </Field>
              )}
              {foreign && (
                <Field id="bill-paid" label={`Or the ${base} amount the bank paid`} hint="Optional: from your bank or card statement, to post at the rate you were charged"
                  error={paid.trim() && !paidRate ? 'An amount such as 987.50' : null}>
                  <input id="bill-paid" className="el-input el-num" inputMode="decimal" disabled={!open} value={paid}
                    onChange={(e) => { setDirty(true); setPaid(e.target.value); }} />
                </Field>
              )}
            </div>
            <div className="el-table-wrap">
              <table className="el-table el-lines" aria-label="Bill lines" style={{ minWidth: 760 }}>
                <thead>
                  <tr>
                    <th scope="col" style={{ width: '30%' }}>Description</th><th scope="col">Account</th>
                    <th scope="col" className="el-num">Qty</th><th scope="col" className="el-num">Unit price</th>
                    <th scope="col">VAT rate</th><th scope="col" className="el-num">VAT</th><th scope="col" className="el-num">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={l.key}>
                      <td><input id={`bl-desc-${i}`} className="el-input" value={l.description} disabled={!open} aria-label={`Line ${i + 1} description`} onChange={(e) => setLine(l.key, { description: e.target.value })} /></td>
                      <td>
                        <select id={`bl-acc-${i}`} className="el-input" value={l.account_code} disabled={!open} aria-label={`Line ${i + 1} account`}
                          aria-invalid={l.account_code === SUSPENSE}
                          onChange={(e) => setLine(l.key, { account_code: e.target.value })}>
                          {l.account_code === SUSPENSE && <option value={SUSPENSE}>Choose an account…</option>}
                          {accounts.map((a) => <option key={a.code} value={a.code}>{a.code} {a.name}</option>)}
                        </select>
                      </td>
                      <td><input id={`bl-qty-${i}`} className="el-input el-num" style={{ width: 70 }} inputMode="decimal" value={l.quantity} disabled={!open} aria-label={`Line ${i + 1} quantity`} onChange={(e) => setLine(l.key, { quantity: e.target.value })} /></td>
                      <td><input id={`bl-price-${i}`} className="el-input el-num" style={{ width: 110 }} inputMode="decimal" value={l.unit_price} disabled={!open} aria-label={`Line ${i + 1} unit price`} onChange={(e) => setLine(l.key, { unit_price: e.target.value })} /></td>
                      <td>
                        <select id={`bl-vat-${i}`} className="el-input" value={l.tax_code} disabled={!open} aria-label={`Line ${i + 1} VAT rate`} onChange={(e) => setLine(l.key, { tax_code: e.target.value, tax_amount: '' })}>
                          {codes.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </select>
                      </td>
                      <td><input id={`bl-tax-${i}`} className="el-input el-num" style={{ width: 96 }} inputMode="decimal" value={l.tax_amount}
                        placeholder={trim(totals.rows[i]!.tax)} disabled={!open} aria-label={`Line ${i + 1} VAT amount`}
                        title="Leave blank to work it out; type the supplier’s figure to match their invoice"
                        onChange={(e) => setLine(l.key, { tax_amount: e.target.value })} /></td>
                      <td className="el-num">{money(totals.rows[i]!.net)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="el-spread" style={{ padding: '14px 20px 0', alignItems: 'flex-start' }}>
              <p className="el-hint" style={{ margin: 0, maxWidth: 320 }}>
                {onSuspense > 0 ? `Choose an account for ${onSuspense === 1 ? 'the highlighted line' : `the ${onSuspense} highlighted lines`} before approving.` : 'The coding is remembered for this supplier’s next bill.'}
                {rateProblem && <span role="status" style={{ display: 'block', color: 'var(--el-warn-ink)', marginTop: 6 }}>{rateProblem}</span>}
              </p>
              <dl className="el-totals" aria-label="Bill totals">
                <div><dt>Net</dt><dd className="el-num">{money(totals.net)}</dd></div>
                <div className="el-muted"><dt>VAT</dt><dd className="el-num">{money(totals.tax)}</dd></div>
                <div className="el-totals-grand"><dt>Total</dt><dd className="el-num">{money(totals.gross)}</dd></div>
                {readGross && (
                  <div style={mismatch ? { color: 'var(--el-warn-ink)' } : { color: 'var(--el-brand)' }}>
                    <dt>{mismatch ? 'The bill says' : 'Matches the bill'}</dt><dd className="el-num">{money(readGross)}</dd>
                  </div>
                )}
                {foreign && rateInUse && (
                  <div className="el-muted"><dt>In {base} at {rateInUse}</dt><dd className="el-num">{formatMoney(inBase(totals.gross)!, base)}</dd></div>
                )}
              </dl>
            </div>
          </Card>
        )}
      </div>

      {ack && (
        <Dialog title="Post anyway?" onClose={() => setAck(null)}
          actions={<><Button variant="ghost" onClick={() => setAck(null)}>Go back and correct it</Button><Button variant="primary" onClick={() => void approve(true)} disabled={busy}>Post anyway</Button></>}>
          <p style={{ margin: 0 }}>{ack}</p>
          <p className="el-hint" style={{ margin: 0 }}>Only post anyway if you’ve checked the bill and the difference is the supplier’s rounding.</p>
        </Dialog>
      )}
      {rejecting && (
        <Dialog title="Remove from the inbox?" onClose={() => setRejecting(false)}
          actions={<><Button variant="ghost" onClick={() => setRejecting(false)}>Keep it</Button><Button variant="danger" onClick={() => void reject()} disabled={busy || reason.trim().length < 3}>Remove</Button></>}>
          <p style={{ margin: 0 }}>Nothing is posted. The file is kept with the reason, in case you need it later.</p>
          <Field id="reject-reason" label="Reason">
            <input id="reject-reason" className="el-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="A duplicate, or a statement rather than a bill" />
          </Field>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}
    </>
  );
}

const MATCH: Record<string, string> = { vat_number: 'VAT number', alias: 'a name seen before', name: 'name', person: 'you' };
const READ_FIELDS: [string, string][] = [
  ['supplier_name', 'Supplier'], ['supplier_vat_number', 'Supplier’s VAT number'], ['invoice_number', 'Invoice no.'],
  ['issue_date', 'Date'], ['due_date', 'Due'], ['currency', 'Currency'], ['net', 'Net'], ['vat', 'VAT'], ['gross', 'Total'],
];

function level(c: number | undefined): 'high' | 'mid' | 'low' {
  return c === undefined ? 'mid' : c >= 0.9 ? 'high' : c >= 0.8 ? 'mid' : 'low';
}

function Confidence({ value }: { value: number | undefined }) {
  const l = level(value);
  return <span className="el-conf" data-level={l} title={value !== undefined ? `${Math.round(value * 100)}% sure` : undefined}>{l === 'high' ? 'Sure' : l === 'mid' ? 'Likely' : 'Check'}</span>;
}

/** The real file when the server has it; otherwise a stand-in drawn from what was read. */
function BillFile({ item, file, who, draft }: { item: InboxItem; file: Blob | null; who: string; draft: LedgerDocument | null }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file) return;
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  if (file && url) {
    return file.type.startsWith('image/')
      ? <img className="el-bill-image" src={url} alt={`Photo of the bill from ${who}`} />
      : <iframe className="el-bill-frame" src={url} title={`The bill from ${who}`} />;
  }
  const mark = (k: string, text: string | undefined) =>
    text ? <span className="el-mark" data-level={level(item.fields?.[k]?.confidence)}>{text}</span> : '—';
  return (
    <div className="el-scan">
      <span className="el-scan-note">Demo: drawn from what was read</span>
      <div className="el-scan-title">{mark('supplier_name', readValue(item, 'supplier_name'))}</div>{/* a picture of the bill, not a heading of this page */}
      {readValue(item, 'supplier_vat_number') && <div>VAT {mark('supplier_vat_number', readValue(item, 'supplier_vat_number'))}</div>}
      <dl>
        <dt>Invoice</dt><dd>{mark('invoice_number', readValue(item, 'invoice_number'))}</dd>
        <dt>Date</dt><dd>{mark('issue_date', readValue(item, 'issue_date'))}</dd>
        <dt>Due</dt><dd>{mark('due_date', readValue(item, 'due_date'))}</dd>
      </dl>
      {draft && (
        <table>
          <tbody>
            {draft.lines.map((l) => <tr key={l.line_no}><td>{l.description}{l.quantity && l.quantity !== '1.0000' && l.quantity !== '1' ? ` × ${trim(l.quantity)}` : ''}</td><td className="el-num">{trim(l.net_amount)}</td></tr>)}
          </tbody>
        </table>
      )}
      <dl>
        <dt>Net</dt><dd>{mark('net', readValue(item, 'net'))}</dd>
        <dt>VAT</dt><dd>{mark('vat', readValue(item, 'vat'))}</dd>
        <dt><strong>Total</strong></dt><dd><strong>{mark('gross', readValue(item, 'gross'))}</strong></dd>
      </dl>
    </div>
  );
}

function trim(v: string): string {
  if (!v || !v.includes('.')) return v;
  const [w, f = ''] = v.split('.');
  const t = f.replace(/0+$/, '');
  return t.length === 0 ? w! : `${w}.${t.padEnd(2, '0')}`;
}

function titleCase(s: string) {
  return s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}
